import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, CreditCard, ArrowRight, Building2, MapPin } from 'lucide-react';
export const Checkout = () => {
  const navigate = useNavigate();
  const handlePay = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/payment-success');
  };
  return <div className="min-h-screen bg-neutral-light pt-24 pb-20" data-id="element-1782">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl" data-id="element-1783">
        <div className="mb-8" data-id="element-1784">
          <Link to="/list-property" className="text-sm font-bold text-text-light hover:text-primary-900 mb-4 inline-block" data-id="element-1785">
            ← Back to Listing Setup
          </Link>
          <h1 className="text-3xl font-serif font-bold text-primary-900 mb-2" data-id="element-1786">
            Complete Payment
          </h1>
          <p className="text-text-light" data-id="element-1787">
            Secure payment for your property listing and verification.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8" data-id="element-1788">
          {/* Left: Summary */}
          <div className="lg:col-span-2 space-y-6" data-id="element-1789">
            <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 p-6" data-id="element-1790">
              <h3 className="font-serif font-bold text-lg text-primary-900 mb-4 border-b border-neutral-100 pb-4" data-id="element-1791">
                Order Summary
              </h3>

              <div className="flex gap-4 mb-6" data-id="element-1792">
                <div className="w-16 h-16 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0" data-id="element-1793">
                  <Building2 className="text-neutral-400" size={24} data-id="element-1794" />
                </div>
                <div data-id="element-1795">
                  <h4 className="font-bold text-text text-sm mb-1" data-id="element-1796">
                    Luxury 5-Bed Duplex
                  </h4>
                  <p className="text-xs text-text-light flex items-center gap-1" data-id="element-1797">
                    <MapPin size={12} data-id="element-1798" /> Maitama District
                  </p>
                  <span className="inline-block mt-2 px-2 py-0.5 bg-secondary/10 text-secondary-dark text-[10px] font-bold rounded uppercase" data-id="element-1799">
                    Tier 1 Listing
                  </span>
                </div>
              </div>

              <div className="space-y-3 text-sm mb-2 pb-2" data-id="element-1800">
                <div className="flex justify-between text-text-light" data-id="element-1801">
                  <span data-id="element-1802">Tier 1 — Individual Listing</span>
                  <span className="font-medium text-text" data-id="element-1803">₦300,000</span>
                </div>
                <div className="flex justify-between text-text-light" data-id="element-1804">
                  <span data-id="element-1805">Verification Processing</span>
                  <span className="font-medium text-text" data-id="element-1806">Included</span>
                </div>
                <div className="flex justify-between text-text-light" data-id="element-1807">
                  <span data-id="element-1808">VAT (7.5%)</span>
                  <span className="font-medium text-text" data-id="element-1809">₦22,500</span>
                </div>
              </div>
              <p className="text-[11px] italic text-text-light leading-relaxed mb-6 pb-6 border-b border-neutral-100" data-id="element-1810">
                Real estate companies receive a reduced rate of ₦250,000 per
                home in estate developments. Tier 2 and Tier 3 fees apply
                elsewhere — see pricing in your seller dashboard.
              </p>

              <div className="flex justify-between items-end" data-id="element-1811">
                <span className="font-bold text-text" data-id="element-1812">Total Due</span>
                <span className="text-2xl font-bold text-primary-900" data-id="element-1813">
                  ₦322,500
                </span>
              </div>
            </div>

            <div className="bg-primary-50 rounded-2xl border border-primary-100 p-5 flex gap-3" data-id="element-1814">
              <ShieldCheck className="text-primary-900 shrink-0" size={24} data-id="element-1815" />
              <p className="text-xs text-primary-900 leading-relaxed" data-id="element-1816">
                <strong data-id="element-1817">Secure Transaction.</strong> Your payment is processed
                securely. The listing fee covers AGIS/FCDA verification and
                platform visibility for 90 days.
              </p>
            </div>
          </div>

          {/* Right: Payment Methods */}
          <div className="lg:col-span-3" data-id="element-1818">
            <div className="bg-white rounded-2xl shadow-soft border border-neutral-200 p-6 md:p-8" data-id="element-1819">
              <h3 className="font-serif font-bold text-lg text-primary-900 mb-6" data-id="element-1820">
                Payment Method
              </h3>

              <form onSubmit={handlePay} className="space-y-6" data-id="element-1821">
                <div className="space-y-3" data-id="element-1822">
                  <label className="flex items-center justify-between p-4 border-2 border-secondary bg-secondary/5 rounded-xl cursor-pointer" data-id="element-1823">
                    <div className="flex items-center gap-3" data-id="element-1824">
                      <input type="radio" name="payment" defaultChecked className="text-secondary focus:ring-secondary w-4 h-4" data-id="element-1825" />
                      <span className="font-bold text-text" data-id="element-1826">
                        Paystack (Card/Transfer)
                      </span>
                    </div>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/0/0b/Paystack_Logo.png" alt="Paystack" className="h-5 object-contain" data-id="element-1827" />
                  </label>

                  <label className="flex items-center justify-between p-4 border border-neutral-200 rounded-xl cursor-pointer hover:bg-neutral-50 transition-colors" data-id="element-1828">
                    <div className="flex items-center gap-3" data-id="element-1829">
                      <input type="radio" name="payment" className="text-secondary focus:ring-secondary w-4 h-4" data-id="element-1830" />
                      <span className="font-bold text-text" data-id="element-1831">Flutterwave</span>
                    </div>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Flutterwave_Logo.svg/1200px-Flutterwave_Logo.svg.png" alt="Flutterwave" className="h-5 object-contain grayscale opacity-70" data-id="element-1832" />
                  </label>

                  <label className="flex items-center justify-between p-4 border border-neutral-200 rounded-xl cursor-pointer hover:bg-neutral-50 transition-colors" data-id="element-1833">
                    <div className="flex items-center gap-3" data-id="element-1834">
                      <input type="radio" name="payment" className="text-secondary focus:ring-secondary w-4 h-4" data-id="element-1835" />
                      <span className="font-bold text-text" data-id="element-1836">
                        Direct Bank Transfer
                      </span>
                    </div>
                    <CreditCard className="text-neutral-400" size={24} data-id="element-1837" />
                  </label>
                </div>

                <div className="pt-6 border-t border-neutral-100" data-id="element-1838">
                  <label className="block text-sm font-bold text-text mb-2" data-id="element-1839">
                    Promo Code (Optional)
                  </label>
                  <div className="flex gap-2" data-id="element-1840">
                    <input type="text" placeholder="Enter code" className="flex-grow px-4 py-3 rounded-xl border border-neutral-200 bg-neutral-50 focus:bg-white focus:ring-2 focus:ring-primary-900 outline-none" data-id="element-1841" />
                    <button type="button" className="px-6 py-3 bg-neutral-200 text-text font-bold rounded-xl hover:bg-neutral-300 transition-colors" data-id="element-1842">
                      Apply
                    </button>
                  </div>
                </div>

                <button type="submit" className="w-full bg-primary-900 hover:bg-primary-800 text-white py-4 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 shadow-lg mt-8" data-id="element-1843">
                  Pay ₦322,500 <ArrowRight size={18} data-id="element-1844" />
                </button>
                <p className="text-center text-xs text-text-light flex items-center justify-center gap-1" data-id="element-1845">
                  <ShieldCheck size={12} data-id="element-1846" /> Payments are encrypted and secure
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
};